/**
 * Formula Evaluator for CustomAttributes Table Calculations
 * 
 * Provides safe formula evaluation for calculated columns in table attributes.
 * Supports basic arithmetic operations: +, -, *, /
 */

/**
 * Validate formula syntax and column references
 * @param {string} formula - Formula string (e.g., "qty * price")
 * @param {Array} columns - Available columns array
 * @returns {Object} Validation result with isValid and error message
 */
export function validateFormula(formula, columns) {
  if (!formula || typeof formula !== 'string' || !formula.trim()) {
    return {
      isValid: false,
      error: 'Formula cannot be empty'
    };
  }

  // Get column keys
  const columnKeys = columns.map(col => col.key).filter(Boolean);
  
  // Extract column references from formula (word boundaries to match whole words only)
  const columnPattern = /\b[a-zA-Z_][a-zA-Z0-9_]*\b/g;
  const matches = formula.match(columnPattern) || [];
  
  // Filter out operators and numbers
  const operators = ['and', 'or', 'not', 'if', 'sum', 'avg', 'max', 'min'];
  const referencedColumns = matches.filter(match => {
    // Skip if it's a number
    if (!isNaN(parseFloat(match))) return false;
    // Skip if it's an operator
    if (operators.includes(match.toLowerCase())) return false;
    return true;
  });

  // Check if all referenced columns exist
  const invalidColumns = referencedColumns.filter(key => !columnKeys.includes(key));
  if (invalidColumns.length > 0) {
    return {
      isValid: false,
      error: `Invalid column references: ${invalidColumns.join(', ')}`
    };
  }

  // Check if formula contains only valid characters
  const validPattern = /^[a-zA-Z0-9_+\-*/().\s]+$/;
  if (!validPattern.test(formula)) {
    return {
      isValid: false,
      error: 'Formula contains invalid characters'
    };
  }

  return {
    isValid: true,
    error: null
  };
}

/**
 * Format formula for display (replace operators with symbols)
 * @param {string} formula - Formula string
 * @param {Array} columns - Available columns array
 * @returns {string} Formatted formula for display
 */
export function formatFormulaDisplay(formula, columns) {
  if (!formula) return '';
  
  // Create column label map
  const columnMap = columns.reduce((acc, col) => {
    acc[col.key] = col.label || col.key;
    return acc;
  }, {});

  let display = formula;
  
  // Replace column keys with labels
  Object.entries(columnMap).forEach(([key, label]) => {
    const regex = new RegExp(`\\b${key}\\b`, 'g');
    display = display.replace(regex, label);
  });

  // Replace operators with display symbols
  display = display.replace(/\*/g, '×');
  display = display.replace(/\//g, '÷');
  
  return display;
}

/**
 * Replace column references in formula with actual values
 * @param {string} formula - Formula string
 * @param {Object} rowData - Row data object
 * @param {Array} columns - Available columns array
 * @returns {string} Formula with values substituted
 */
function replaceColumnReferences(formula, rowData, columns) {
  let expression = formula;
  
  // Replace each column reference with its value
  columns.forEach(col => {
    if (col.key) {
      const value = rowData[col.key];
      let numericValue;
      
      if (value === null || value === undefined || value === '') {
        numericValue = 0;
      } else {
        numericValue = parseFloat(value);
        if (isNaN(numericValue)) {
          numericValue = 0;
        }
      }
      
      // Use word boundaries to match whole column keys only
      const regex = new RegExp(`\\b${col.key}\\b`, 'g');
      expression = expression.replace(regex, numericValue.toString());
    }
  });
  
  return expression;
}

/**
 * Evaluate formula for a given row
 * @param {string} formula - Formula string (e.g., "qty * price")
 * @param {Object} rowData - Row data object
 * @param {Array} columns - Available columns array
 * @returns {number|null} Calculated value or null if error
 */
export function evaluateFormula(formula, rowData, columns) {
  if (!formula || typeof formula !== 'string') {
    return null;
  }

  try {
    // Validate formula first
    const validation = validateFormula(formula, columns);
    if (!validation.isValid) {
      console.warn('Formula validation failed:', validation.error);
      return null;
    }

    // Replace column references with values
    let expression = replaceColumnReferences(formula, rowData, columns);
    
    // Remove any remaining non-numeric, non-operator characters (safety check)
    expression = expression.replace(/[^0-9+\-*/().\s]/g, '');
    
    // Evaluate safely using Function constructor with strict mode
    // This prevents access to global scope
    const result = Function('"use strict"; return (' + expression + ')')();
    
    // Check for valid number result
    if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
      return null;
    }
    
    return result;
  } catch (error) {
    console.error('Formula evaluation error:', error, {
      formula,
      rowData,
      errorMessage: error.message
    });
    return null;
  }
}

/**
 * Calculate totals for columns with showTotal flag
 * @param {Array} rows - Array of row data objects
 * @param {Array} columns - Available columns array
 * @param {Function} evaluateFormulaFn - Formula evaluation function
 * @returns {Object} Object with column keys and their totals
 */
export function calculateTotals(rows, columns, evaluateFormulaFn = evaluateFormula) {
  const totals = {};
  
  columns.forEach(col => {
    if (col.showTotal) {
      if (col.type === 'number') {
        // Sum regular number columns
        totals[col.key] = rows.reduce((sum, row) => {
          const value = parseFloat(row[col.key]) || 0;
          return sum + value;
        }, 0);
      } else if (col.type === 'calculated' && col.formula) {
        // Sum calculated columns
        totals[col.key] = rows.reduce((sum, row) => {
          const calculated = evaluateFormulaFn(col.formula, row, columns);
          return sum + (calculated || 0);
        }, 0);
      }
    }
  });
  
  return totals;
}

/**
 * Get available number columns for formula builder
 * @param {Array} columns - All columns array
 * @returns {Array} Filtered array of number columns only
 */
export function getNumberColumns(columns) {
  return columns.filter(col => 
    col.type === 'number' && 
    col.key && 
    col.type !== 'calculated'
  );
}

/**
 * Parse formula into tokens for display/editing
 * @param {string} formula - Formula string
 * @returns {Array} Array of tokens with type and value
 */
export function parseFormulaTokens(formula) {
  if (!formula) return [];
  
  const tokens = [];
  const operators = ['+', '-', '*', '/', '(', ')'];
  let currentToken = '';
  
  for (let i = 0; i < formula.length; i++) {
    const char = formula[i];
    
    if (operators.includes(char) || char === ' ') {
      if (currentToken.trim()) {
        tokens.push({
          type: isNaN(parseFloat(currentToken)) ? 'column' : 'number',
          value: currentToken.trim()
        });
        currentToken = '';
      }
      
      if (operators.includes(char)) {
        tokens.push({
          type: 'operator',
          value: char
        });
      }
    } else {
      currentToken += char;
    }
  }
  
  if (currentToken.trim()) {
    tokens.push({
      type: isNaN(parseFloat(currentToken)) ? 'column' : 'number',
      value: currentToken.trim()
    });
  }
  
  return tokens;
}
